import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  padding: 1rem;
`;

const ImageContainer = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Image = styled.img`
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 0.5rem;
  box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.2);
`;

const CloseButton = styled.button`
  position: absolute;
  top: -2.5rem;
  right: -2.5rem;
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  background: transparent;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    top: -3rem;
    right: 0;
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ direction }) => (direction === 'left' ? 'left: -3.75rem;' : 'right: -3.75rem;')}
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 1024px) {
    ${({ direction }) => (direction === 'left' ? 'left: -3rem;' : 'right: -3rem;')}
  }

  @media (max-width: 768px) {
    ${({ direction }) => (direction === 'left' ? 'left: 0.625rem;' : 'right: 0.625rem;')}
    width: 2.25rem;
    height: 2.25rem;
    font-size: 1.25rem;
  }

  @media (max-width: 480px) {
    ${({ direction }) => (direction === 'left' ? 'left: 0.5rem;' : 'right: 0.5rem;')}
    width: 2rem;
    height: 2rem;
    font-size: 1rem;
  }
`;

const Caption = styled.div`
  position: absolute;
  bottom: -2.5rem;
  left: 0;
  right: 0;
  text-align: center;
  color: white;
  font-size: 0.875rem;
  padding: 0 1rem;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    bottom: -2rem;
  }
`;

const ImagePreview = ({
  visible,
  onClose,
  images = [],
  initialIndex = 0,
  captions = []
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [visible]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setLoading(true);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setLoading(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      handlePrevious();
    } else if (e.key === 'ArrowRight') {
      handleNext();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  if (!visible || images.length === 0) return null;

  return (
    <Overlay visible={visible} onClick={onClose}>
      <ImageContainer onClick={(e) => e.stopPropagation()}>
        <Image
          src={images[currentIndex]}
          alt={captions[currentIndex] || `Image ${currentIndex + 1}`}
          onLoad={() => setLoading(false)}
          style={{ display: loading ? 'none' : 'block' }}
          crossOrigin="anonymous"
        />
        <CloseButton onClick={onClose}>
          <CloseOutlined />
        </CloseButton>
        {images.length > 1 && (
          <>
            <NavigationButton
              direction="left"
              onClick={(e) => {
                e.stopPropagation();
                handlePrevious();
              }}
            >
              <LeftOutlined />
            </NavigationButton>
            <NavigationButton
              direction="right"
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
            >
              <RightOutlined />
            </NavigationButton>
            <Caption>
              {captions[currentIndex] ||
                `${currentIndex + 1} / ${images.length}`}
            </Caption>
          </>
        )}
      </ImageContainer>
    </Overlay>
  );
};

export default ImagePreview; 